import { useCallback, useEffect, useMemo, useState } from "react";

import { NotificationsService } from "@services/NotificationsService";

export interface NotificacionDTO {
    fromName: string;
    fromUserID: string;
    buildingName: string;
    buildingID: string;
    message: string;
    type: string;
    timestamp: number;
    expiryTimestamp?: number;
}

export interface NotificacionListItem {
    name: string;
    userID: string;
    description: string;
    building: string;
    buildingID: string;
    categoria: string;
    timestamp: number;
    expiryTimestamp?: number;
}

interface UseNotificacionesListScreenViewModelProps {
    orgID: string;
    notificationsService: NotificationsService;
}

const PAGE_SIZE = "25";

const mapNotification = (row: NotificacionDTO): NotificacionListItem => ({
    name: row.fromName,
    userID: row.fromUserID,
    description: row.message,
    building: row.buildingName,
    buildingID: row.buildingID,
    categoria: row.type,
    timestamp: row.timestamp,
    expiryTimestamp: row.expiryTimestamp,
});

export const processNotificationName = (name: string) => {
    return name
        .split(" ")
        .map((word) => {
            const firstLetter = word.charAt(0).toUpperCase();
            const restOfWord = word.slice(1).toLowerCase();
            return firstLetter + restOfWord;
        })
        .join(" ");
};

export const useNotificacionesListScreenViewModel = ({
    orgID,
    notificationsService,
}: UseNotificacionesListScreenViewModelProps) => {
    const [lastKey, setLastKey] = useState("");
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<NotificacionListItem[]>([]);
    const [lastReadTimestamp, setLastReadTimestamp] = useState(0);
    const [mostrarSoloNoLeidos, setMostrarSoloNoLeidos] = useState(false);
    const [searchField, setSearchField] = useState("");

    const fetchNotifications = useCallback(async () => {
        if (loading || !orgID) return;

        setLoading(true);
        try {
            const json = await notificationsService.fetchNotifications(orgID, PAGE_SIZE, lastKey);

            setLastKey(json.lastKey);
            setHasMore(json.data.length > 0);
            setRows((previousRows) => [...previousRows, ...json.data.map(mapNotification)]);
        } finally {
            setLoading(false);
        }
    }, [lastKey, loading, notificationsService, orgID]);

    const refreshLastReadTimestamp = useCallback(async () => {
        if (!orgID) return;

        const json = await notificationsService.fetchLastReadTimestamp(orgID);
        setLastReadTimestamp(json?.lastReadTimestamp ?? 0);
    }, [notificationsService, orgID]);

    useEffect(() => {
        setLastKey("");
        setHasMore(true);
        setRows([]);
        setLastReadTimestamp(0);
        setMostrarSoloNoLeidos(false);
        setSearchField("");
    }, [orgID]);

    useEffect(() => {
        void refreshLastReadTimestamp();
    }, [refreshLastReadTimestamp]);

    useEffect(() => {
        if (!hasMore) return;
        void fetchNotifications();
    }, [fetchNotifications, hasMore]);

    const unreadCount = useMemo(() => {
        return rows.filter((row) => row.timestamp > lastReadTimestamp).length;
    }, [rows, lastReadTimestamp]);

    const filteredRows = useMemo(() => {
        const normalizedSearchField = searchField.toLowerCase().replace(/\s/g, "");

        return rows
            .filter((row) => {
                if (!normalizedSearchField) {
                    return true;
                }

                const normalizedName = row.name
                    .toLowerCase()
                    .replace(/\s/g, "");
                const normalizedCategory = row.categoria
                    .toLowerCase()
                    .replace(/\s/g, "");

                return normalizedName.includes(normalizedSearchField)
                    || normalizedCategory.includes(normalizedSearchField);
            })
            .filter((row) => {
                const isRead = lastReadTimestamp >= row.timestamp;
                return mostrarSoloNoLeidos ? !isRead : true;
            })
            .sort((a, b) => b.timestamp - a.timestamp);
    }, [lastReadTimestamp, mostrarSoloNoLeidos, rows, searchField]);

    const isRead = useCallback((row: NotificacionListItem) => {
        return lastReadTimestamp >= row.timestamp;
    }, [lastReadTimestamp]);

    const toggleMostrarSoloNoLeidos = useCallback(() => {
        setMostrarSoloNoLeidos((current) => !current);
    }, []);

    const markAllAsRead = useCallback(async () => {
        if (!orgID) return;

        await notificationsService.updateLastReadTimestamp(orgID);
        setLastReadTimestamp(Date.now());
    }, [notificationsService, orgID]);

    return {
        loading,
        filteredRows,
        unreadCount,
        mostrarSoloNoLeidos,
        searchField,
        setSearchField,
        toggleMostrarSoloNoLeidos,
        markAllAsRead,
        isRead,
    };
};
